import { useEffect, useMemo, useState } from 'react'
import { watchGame, setScore } from './ggGamesData.js'
import { getPlayable } from './ggGameRegistry.js'
import { getGameUI } from './ggGameUI.jsx'
import { wolfStrokeGrid } from './ggPlayWolf.js'
import { scoreEmoji, SCORE_LEGEND } from './ggScoreHelpers.js'
import GScatsLive from './GScatsLive.jsx'
import GSkinsLive from './GSkinsLive.jsx'
import GDotsPanel from './GDotsPanel.jsx'
import CLUB from '../../config/club.js'

// Universal live scorecard (frame), mirroring the app's Wolf play screen:
// header (real allowance), a Leader strip of running totals, the game's per-hole
// inputs (Wolf pick + bet cards), per-player cards with −/+ steppers, score
// emoji, running money + W-L + role badge, the Scats panel, the emoji legend,
// and Prev / hole / Next at the bottom.
const GREEN = '#1B4D2E'

// Whole dollars only — mirrors the app's card: `Math.round(net)`, never cents.
const money = (v) => (Math.round(v) === 0 ? '$0' : v > 0 ? `+$${Math.round(v)}` : `−$${Math.round(-v)}`)
const scoreBg = (score, par) => {
  if (score == null) return '#F0F0F0'
  if (score === 1) return '#1565C0'
  if (score === 8) return '#4E342E'
  const d = score - par
  if (d <= -2) return '#C8E6C9'
  if (d === -1) return '#DCEDC8'
  if (d === 1) return '#FFE0B2'
  if (d >= 2) return '#FFCDD2'
  return '#F0F0F0'
}

export default function GScorecard({ gameId, onFinish, onExit }) {
  const [game, setGame] = useState(null)
  const [hole, setHole] = useState(1)
  useEffect(() => watchGame(gameId, setGame), [gameId])

  const players = game?.players || []
  const meta = game?.meta || {}
  const course = meta.course || {}
  const reg = getPlayable(meta.gameId)
  const ui = getGameUI(meta.gameId)

  const result = useMemo(() => {
    if (!reg || players.length === 0) return null
    try { return reg.settle(players, game.scores || {}, meta.options || {}, { strokeIndex: course.strokeIndex, pars: course.pars, handicapsOn: meta.handicapsOn, teams: meta.teams, ...game, wolf: game.wolf || {}, wolfBet: game.wolfBet || {} }) } catch { return null }
  }, [reg, players, game, meta.options, course.strokeIndex])

  if (!game) return <div className="card">Loading…</div>
  const par = course.pars?.[hole - 1] ?? 4
  const si = course.strokeIndex?.[hole - 1] ?? hole
  const roles = ui.rolesFor ? ui.rolesFor(game, hole) : {}

  // Per-hole handicap strokes (who gets a stroke this hole), by the game's mode.
  const strokesById = {}
  if (meta.handicapsOn) {
    const grid = wolfStrokeGrid(players, meta.options?.strokeMode || 'lowest', course.strokeIndex)
    players.forEach((p, i) => { strokesById[p.id] = grid[i]?.[hole - 1] || 0 })
  }
  // Current-hole stake + carry (so a carried bet is visible, not mistaken for per-winner).
  const holeRec = result?.holes?.[hole - 1]
  const baseBet = Number(meta.options?.baseBet) || 0
  const carry = holeRec ? Math.max(0, holeRec.holeValue - baseBet) : 0

  // Real allowance for the header (per-player now): show the common %, else "mixed".
  const allowLabel = (() => {
    if (!meta.handicapsOn) return 'Gross'
    const set = [...new Set(players.map((p) => p.allowance ?? 100))]
    return set.length === 1 ? `Net · ${set[0]}%` : 'Net · mixed %'
  })()

  const runningTotal = (pid) => {
    let t = 0
    for (let h = 1; h <= 18; h++) { const s = Number(game.scores?.[h]?.[pid]); if (Number.isFinite(s) && s > 0) t += s }
    return t
  }
  const leaderId = (() => {
    const totals = players.map((p) => ({ id: p.id, t: runningTotal(p.id) })).filter((x) => x.t > 0)
    if (!totals.length) return null
    const min = Math.min(...totals.map((x) => x.t))
    const low = totals.filter((x) => x.t === min)
    return low.length === 1 ? low[0].id : null
  })()

  const step = (pid, delta) => {
    const cur = Number(game.scores?.[hole]?.[pid])
    const base = Number.isFinite(cur) && cur > 0 ? cur : par
    setScore(gameId, hole, pid, Math.max(1, Math.min(20, base + delta)))
  }
  const next = () => {
    // Confirm par for any untapped player (mirrors the app's flag-on-Next), then advance.
    players.forEach((p) => { if (!Number.isFinite(Number(game.scores?.[hole]?.[p.id]))) setScore(gameId, hole, p.id, par) })
    if (hole === 18) onFinish(); else setHole((h) => h + 1)
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-bold text-lg">{meta.name}</div>
          <div className="text-xs text-gray-500">{reg?.label} · {course.name} · {allowLabel} · {players.length} players</div>
        </div>
        <button className="btn-ghost text-xs" onClick={onExit}>Save &amp; Exit</button>
      </div>

      {/* Leader strip — running totals */}
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        {players.map((p) => {
          const t = runningTotal(p.id)
          return (
            <span key={p.id} className="px-2 py-1 rounded-full" style={{ background: p.id === leaderId ? '#E8F5E9' : '#F5F5F5', border: p.id === leaderId ? `1px solid ${GREEN}` : '1px solid #E0E0E0', fontWeight: p.id === leaderId ? 800 : 600 }}>
              {p.id === leaderId ? '👑 ' : ''}{p.name.split(' ')[0]} {t || '–'}
            </span>
          )
        })}
      </div>

      {/* Game-specific per-hole inputs (Wolf pick + bet cards) */}
      {ui.HoleInputs && <ui.HoleInputs gameId={gameId} game={game} hole={hole} />}

      {/* Stake this hole (surfaces carry so it isn't mistaken for per-winner) */}
      {holeRec && baseBet > 0 && (
        <div className="text-xs mb-2 text-gray-600">Stake this hole: <b>${Math.round(holeRec.holeValue)}</b>{carry > 0 ? ` (incl. $${Math.round(carry)} carry)` : ''} · {meta.options?.wolfPayout === 'per-winner' ? 'per-winner' : 'split-pot'}</div>
      )}

      {/* Player cards — roll up each hole so the current Wolf is on top (app's
          cardOrder), then stepper + emoji + running money/W-L/role. */}
      <div className="space-y-2">
        {(ui.cardOrder ? ui.cardOrder(game, hole) : players).map((p) => {
          // Score DEFAULTS to par (shown, never blank); steppers adjust from there.
          const stored = Number(game.scores?.[hole]?.[p.id])
          const hasStored = Number.isFinite(stored) && stored > 0
          const shown = hasStored ? stored : par
          const emoji = scoreEmoji(shown, par)
          const net = result?.nets?.[p.id] ?? 0
          const wl = result?.wl?.[p.id]
          const badge = roles[p.id]
          return (
            <div key={p.id} className="rounded-lg p-3" style={{ border: badge ? `1.5px solid ${GREEN}` : '1px solid #E5E7EB', background: CLUB.creamColor }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-bold">{p.name}</span>
                  {badge && <span className="text-xs font-bold" style={{ color: GREEN }}>{badge}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => step(p.id, -1)} style={{ width: 34, height: 34, borderRadius: 17, border: '1px solid #DDE5E1', background: '#F5FAF6', fontSize: 20, fontWeight: 800, color: '#386641' }}>−</button>
                  <div style={{ width: 56, height: 40, borderRadius: 10, background: scoreBg(shown, par), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, opacity: hasStored ? 1 : 0.85 }}>
                    <span style={{ fontSize: 14 }}>{emoji}</span>
                    <span style={{ fontSize: 20, fontWeight: 900, color: '#0F2914' }}>{shown}</span>
                  </div>
                  <button onClick={() => step(p.id, 1)} style={{ width: 34, height: 34, borderRadius: 17, border: '1px solid #DDE5E1', background: '#F5FAF6', fontSize: 20, fontWeight: 800, color: '#386641' }}>+</button>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {meta.handicapsOn && <span>CH {p.courseHandicap} → PH {p.playingHandicap}</span>}
                {meta.handicapsOn && strokesById[p.id] > 0 && (
                  <span title="strokes this hole"><span style={{ color: '#F9A825', fontWeight: 900, letterSpacing: -1 }}>{'●'.repeat(Math.min(strokesById[p.id], 2))}</span> gets {strokesById[p.id]}{hasStored ? ` · net ${shown - strokesById[p.id]}` : ''}</span>
                )}
                <span>Total {runningTotal(p.id) || '–'}</span>
                {wl && (wl.w + wl.l > 0) ? <span>🐺 {wl.w}-{wl.l}</span> : null}
                <span className="ml-auto font-bold" style={{ color: net > 0 ? '#1B7A3E' : net < 0 ? '#B42318' : '#888' }}>🐺 {money(net)}</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Scats + Skins + Dots live panels */}
      <GScatsLive game={game} hole={hole} />
      <GSkinsLive game={game} hole={hole} />
      <GDotsPanel gameId={gameId} game={game} hole={hole} />

      {/* Emoji legend */}
      <p className="text-[11px] text-gray-500 mt-3" style={{ lineHeight: 1.7 }}>{SCORE_LEGEND}</p>

      {/* Bottom nav */}
      <div className="flex items-center justify-between mt-3">
        <button className="btn-ghost" disabled={hole === 1} onClick={() => setHole((h) => Math.max(1, h - 1))}>← Prev</button>
        <div className="text-center">
          <div className="text-xs uppercase tracking-widest text-gray-500">Hole</div>
          <div className="text-xl font-extrabold">{hole} <span className="text-xs text-gray-400 font-normal">par {par} · SI {si}</span></div>
        </div>
        <button className="btn-primary" style={{ backgroundColor: GREEN }} onClick={next}>{hole === 18 ? '🏁 Finish' : 'Next →'}</button>
      </div>
    </div>
  )
}
